/**
 * Microsoft Clarity helpers.
 *
 * The Clarity tag in `public/index.html` collects anonymous heatmap +
 * session-recording data on every page. These helpers add three layers
 * on top of that:
 *
 *   1. `identifyClarity(user)` — links the current Clarity session to a
 *      `user_id` so we can search recordings by user from the Clarity UI.
 *      Called from AuthContext on login / signup / token resume.
 *   2. `tagClarity(key, value)` — sets a custom tag on the session (e.g.
 *      `plan=trial`, `product=spacecost`) so we can filter recordings by
 *      business state in the dashboard.
 *   3. `resetClarity()` — clears the identification on logout so the next
 *      visitor isn't accidentally bucketed under the previous user.
 *
 * All helpers no-op when the `clarity` global isn't on `window` (e.g. ad-
 * blockers, dev environments, or the very first paint before the async
 * tag has finished loading). Never throws — Clarity must never be allowed
 * to break the app.
 */
function _clarity(...args) {
  try {
    if (typeof window === "undefined") return;
    const c = window.clarity;
    if (typeof c !== "function") return;
    c(...args);
  } catch {
    /* swallow — analytics must never crash the app */
  }
}

/**
 * Link the current session to a user. Pass the user doc from AuthContext.
 *  - `customId` (required by Clarity) → our `user_id`.
 *  - `customSessionId` (optional)     → omitted; we let Clarity manage it.
 *  - `customPageId` (optional)        → omitted.
 *  - `friendlyName` (optional)        → name or email so sessions are
 *    easier to spot in the Clarity dashboard list.
 */
export function identifyClarity(user) {
  if (!user || !user.user_id) return;
  _clarity(
    "identify",
    user.user_id,
    undefined,
    undefined,
    user.name || user.email || undefined,
  );
  // Tag the role + plan so we can filter recordings by paying-vs-free etc.
  if (user.role) _clarity("set", "role", String(user.role));
  if (user.plan) _clarity("set", "plan", String(user.plan));
}

export function tagClarity(key, value) {
  if (!key) return;
  _clarity("set", String(key), value == null ? "" : String(value));
}

export function resetClarity() {
  // Clarity's official "logout" pattern is to re-identify with an empty
  // custom id; that detaches the session from the previous user.
  _clarity("identify", "", undefined, undefined, "anonymous");
}
